import React from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
  FormControlLabel, Grid, MenuItem, Paper, Slider, Stack, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Typography, TextField,
} from '@mui/material';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { apiRequest } from '../api';

interface DomainPrice {
  tld: string;
  price: number;
  setup_fee: number;
}

type ProductChoice = 'domain' | 'vserver';
type DiskType = 'hdd' | 'nvme';

const euro = (amount: number) => amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });

const BestellungPage: React.FC = () => {
  const { token } = useAuth();
  const location = useLocation();
  const [choice, setChoice] = React.useState<ProductChoice | null>(null);
  const [domainPrices, setDomainPrices] = React.useState<DomainPrice[]>([]);
  const [loadingPrices, setLoadingPrices] = React.useState(true);
  const [requestingAccess, setRequestingAccess] = React.useState(false);
  const [ordering, setOrdering] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [cpu, setCpu] = React.useState(2);
  const [ram, setRam] = React.useState(4);
  const [operatingSystem, setOperatingSystem] = React.useState<'Debian' | 'Ubuntu'>('Debian');
  const [diskType, setDiskType] = React.useState<DiskType>('hdd');
  const [disk, setDisk] = React.useState(100);
  const [ipv4, setIpv4] = React.useState(true);

  React.useEffect(() => {
    const navigationState = location.state as { reopenOrderChoice?: boolean } | null;
    if (navigationState?.reopenOrderChoice) setChoice(null);
  }, [location.key, location.state]);

  React.useEffect(() => {
    if (!token) return;
    apiRequest<DomainPrice[]>('/account/domain-prices', token)
      .then((prices) => setDomainPrices(prices))
      .catch((requestError: Error) => setError(requestError.message))
      .finally(() => setLoadingPrices(false));
  }, [token]);

  const minimumDisk = diskType === 'hdd' ? 100 : 40;
  const diskStep = diskType === 'hdd' ? 100 : 20;
  const cpuPrice = cpu >= 5 ? cpu * 2 : cpu;
  const ramPrice = ram > 16 ? ram * 3 : ram;
  const monthlyPrice = cpuPrice + ramPrice + (disk / diskStep) * 0.75 + (ipv4 ? 1 : 0);

  const chooseProduct = (nextChoice: ProductChoice) => {
    setChoice(nextChoice);
    setError(null);
    setSuccess(null);
  };

  const requestDomainAccess = async () => {
    if (!token) return;
    setRequestingAccess(true);
    setError(null);
    setSuccess(null);
    try {
      await apiRequest<{ message: string }>('/account/domain-access-request', token, { method: 'POST' });
      setSuccess('Ihre Anfrage wurde gesendet.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Die Anfrage konnte nicht gesendet werden.');
    } finally {
      setRequestingAccess(false);
    }
  };

  const orderVServer = async () => {
    if (!token) return;
    setOrdering(true);
    setError(null);
    setSuccess(null);
    try {
      await apiRequest<{ message: string }>('/account/orders/vserver', token, {
        method: 'POST',
        body: JSON.stringify({ cpu, ram_gb: ram, operating_system: operatingSystem, disk_type: diskType, disk_gb: disk, ipv4, price: monthlyPrice }),
      });
      setSuccess('Ihre vServer-Bestellung wurde übermittelt. Wir melden uns bei Ihnen.');
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Die Bestellung konnte nicht übermittelt werden.');
    } finally {
      setOrdering(false);
    }
  };

  const setDiskTechnology = (_event: React.MouseEvent<HTMLElement>, value: DiskType | null) => {
    if (!value) return;
    setDiskType(value);
    setDisk(value === 'hdd' ? 100 : 40);
  };

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>Bestellung</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>Wählen Sie aus, welche Leistung Sie bestellen möchten.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {choice === 'domain' && (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" component="h3">Domainpreise</Typography>
              <Typography color="text.secondary">Preise pro Jahr zuzüglich einmaliger Einrichtungsgebühr. Weitere 800 TLDs Preise auf Anfrage.</Typography>
            </Box>
            <Button onClick={() => setChoice(null)}>Produkt wechseln</Button>
          </Stack>
          <Alert severity="info" sx={{ mb: 2 }}>
            Domains können über die Verwaltungsoberfläche: <a href="https://partner.routing.net/" target="_blank" rel="noreferrer">partner.routing.net</a> bestellt werden.
          </Alert>
          <TableContainer sx={{ mb: 3 }}>
            <Table>
              <TableHead><TableRow><TableCell>Domainendung</TableCell><TableCell align="right">Preis / Jahr</TableCell><TableCell align="right">Einrichtungsgebühr</TableCell></TableRow></TableHead>
              <TableBody>
                {loadingPrices ? (
                  <TableRow><TableCell colSpan={3} align="center"><CircularProgress size={26} sx={{ my: 1 }} /></TableCell></TableRow>
                ) : domainPrices.map((price) => (
                  <TableRow key={price.tld} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{price.tld}</TableCell>
                    <TableCell align="right">{euro(Number(price.price))}</TableCell>
                    <TableCell align="right">{euro(Number(price.setup_fee))}</TableCell>
                  </TableRow>
                ))}
                {!loadingPrices && domainPrices.length === 0 && <TableRow><TableCell colSpan={3} align="center">Derzeit sind keine Domainpreise hinterlegt.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <Button variant="contained" onClick={requestDomainAccess} disabled={requestingAccess}>
            {requestingAccess ? 'Anfrage wird gesendet …' : 'Zugang anfragen'}
          </Button>
        </Paper>
      )}

      {choice === 'vserver' && (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1} sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" component="h3">vServer konfigurieren</Typography>
              <Typography color="text.secondary">Der angezeigte Preis ist der monatliche Grundpreis.</Typography>
            </Box>
            <Button onClick={() => setChoice(null)}>Produkt wechseln</Button>
          </Stack>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography gutterBottom>CPU: {cpu} vCPUs · {euro(cpuPrice)} / Monat</Typography>
              <Slider aria-label="CPU" min={2} max={12} step={1} value={cpu} valueLabelDisplay="auto" onChange={(_event, value) => setCpu(value as number)} />
              <Typography variant="caption" color="text.secondary">Mindestens 2 vCPUs · 1,00 € je vCPU</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>Arbeitsspeicher: {ram} GB · {euro(ram)} / Monat</Typography>
              <Slider aria-label="Arbeitsspeicher" min={4} max={64} step={1} value={ram} valueLabelDisplay="auto" onChange={(_event, value) => setRam(value as number)} />
              <Typography variant="caption" color="text.secondary">1,00 € pro GB, mindestens 4 GB</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography gutterBottom>Speicher</Typography>
              <ToggleButtonGroup value={diskType} exclusive onChange={setDiskTechnology} size="small" aria-label="Speichertechnik" sx={{ mb: 1 }}>
                <ToggleButton value="hdd">HDD</ToggleButton>
                <ToggleButton value="nvme">NVMe</ToggleButton>
              </ToggleButtonGroup>
              <Typography gutterBottom>{disk} GB · {euro((disk / diskStep) * 0.75)} / Monat</Typography>
              <Slider aria-label="Speicherplatz" min={minimumDisk} max={diskType === 'hdd' ? 2000 : 2000} step={diskStep} value={disk} valueLabelDisplay="auto" valueLabelFormat={(value) => `${value} GB`} onChange={(_event, value) => setDisk(value as number)} />
              <Typography variant="caption" color="text.secondary">{diskType === 'hdd' ? '0,75 € je 100 GB' : '0,75 € je 20 GB'} · Minimum {minimumDisk} GB</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={ipv4} onChange={(event) => setIpv4(event.target.checked)} />} label={`IPv4-Adresse +${euro(1)} / Monat`} />
                </Grid><Grid item xs={12}>
              <FormControlLabel control={<Switch checked disabled />} label="IPv6 /64 · inklusive" />
            </Grid>
          </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Betriebssystem" value={operatingSystem} onChange={(event) => setOperatingSystem(event.target.value as 'Debian' | 'Ubuntu')}>
                <MenuItem value="Debian">Debian · 0,00 €</MenuItem>
                <MenuItem value="Ubuntu">Ubuntu · 0,00 €</MenuItem>
              </TextField>
            </Grid>
          <Paper variant="outlined" sx={{ p: 2, mt: 3, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box><Typography color="text.secondary" variant="body2">Monatlicher Gesamtpreis</Typography><Typography variant="h4" color="primary.main" fontWeight={700}>{euro(monthlyPrice)}</Typography></Box>
            <Button variant="contained" size="large" onClick={orderVServer} disabled={ordering}>
              {ordering ? 'Bestellung wird gesendet …' : 'Bestellen'}
            </Button>
          </Paper>
          <Typography variant="caption" color="text.secondary">* 1 TB Traffic ist inklusive. Mindestvertragslaufzeit 1 Monat, danach verlänger sich ein Vertrag um einen Monat. 
Ab 1 TB HDD beträgt die Mindestvertragslaufzeit 12 Monate, danach verlänger sich ein Vertrag um einen Monat. Individuelle Konfigurationen auf Anfrage. Ein Kombination aus NVME und HDD ist nicht möglich.</Typography>
        </Paper>
      )}

      <Dialog open={choice === null} onClose={() => {}} aria-labelledby="order-choice-title">
        <DialogTitle id="order-choice-title">Was möchten Sie bestellen?</DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ py: 1 }}>
            <Button variant="outlined" size="large" startIcon={<LanguageOutlinedIcon />} onClick={() => chooseProduct('domain')} sx={{ py: 2, px: 3 }}>Domain</Button>
            <Button variant="outlined" size="large" startIcon={<DnsOutlinedIcon />} onClick={() => chooseProduct('vserver')} sx={{ py: 2, px: 3 }}>vServer</Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BestellungPage;
